import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  MenuItem,
  Typography,
  alpha,
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  Person as PersonIcon,
  ChatBubbleOutline as ComplaintIcon,
  Notes as NotesIcon,
  Science as LabIcon,
  EventNote as EventNoteIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { FaStethoscope } from "react-icons/fa";
import { useClinicTheme } from "../../../theme";

import { COLORS, ACCENTS, TYPO } from "../../../styles/uiConstants";
import {
  ActionButton,
  DrawerHeader,
  FormSelect,
  FormTextField,
} from "../../../components/atoms";
import {
  useStartExaminationMutation,
  useUpdateAppointmentScheduleMutation,
  useListClinicDoctorsMutation,
} from "../../../store";
import useAuth from "../../../hooks/useAuth";
import { useHasPermission } from "../../../hooks/useHasPermission";
import useExaminationAction from "../../../hooks/useExaminationAction";
import { useAlert } from "../../../context/AlertProvider";
import EmptyState from "../../../components/atoms/EmptyState";
import AppointmentBillingSection from "../../../components/organisms/Billing/AppointmentBillingSection";
import FormDatePicker from "../../../components/atoms/FormDatePicker";
import { staffLabel } from "../../../utils/staffName";
import useTerms from "../../../hooks/useTerms";
import { clinical } from "../../../terms";

const getStatusInfo = (status, terms = clinical) => {
  const key = String(status || "scheduled").toLowerCase();
  const map = {
    scheduled: { label: "Planlandı", color: COLORS.catBlueDark, bg: COLORS.catBlueLight },
    in_examination: { label: terms.inExam, color: COLORS.catPurpleDark, bg: COLORS.catPurpleLight },
    cancelled: { label: "İptal Edildi", color: COLORS.errorDark, bg: COLORS.errorLightest },
    completed: { label: "Tamamlandı", color: COLORS.successDark, bg: COLORS.successLight },
    finished: { label: "Tamamlandı", color: COLORS.successDark, bg: COLORS.successLight },
    no_show: { label: "Gelmedi", color: COLORS.warningDark, bg: COLORS.warningLightest },
    encounter: { label: "Görüşme", color: ACCENTS.violet, bg: alpha(ACCENTS.violet, 0.08) },
  };
  return map[key] || map.scheduled;
};

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        bgcolor: COLORS.slate100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.black, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

/**
 * Ortak Randevu Detay Drawer bileşeni.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {Object} selectedAppointment
 * @param {Function} getDoctorName
 * @param {Function} getRefId
 * @param {Function} getAppointmentTypeStyle
 * @param {Function} getAppointmentTypeMeta
 * @param {Array} labTests
 * @param {string} [subtitle] - Header alt başlığı (opsiyonel, ör: hasta adı)
 * @param {ReactNode} [extraContent] - İçerik alanına eklenen ekstra bölüm (edit alanları vb.)
 * @param {ReactNode} [footer] - Alt kısım (butonlar vb.)
 */
export default function AppointmentDetailDrawer({
  open,
  onClose,
  selectedAppointment,
  getDoctorName,
  getRefId,
  getAppointmentTypeStyle,
  getAppointmentTypeMeta,
  labTests,
  subtitle,
  extraContent,
  footer,
}) {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const terms = useTerms();
  const [startExamination, { isLoading: isStartingExamination }] =
    useStartExaminationMutation();
  const examAction = useExaminationAction(selectedAppointment);
  const canUpdateAppointment = useHasPermission("appointments:update");
  const { showError, showSuccess } = useAlert();
  const [updateSchedule, { isLoading: isSavingSchedule }] =
    useUpdateAppointmentScheduleMutation();
  const [listClinicDoctors] = useListClinicDoctorsMutation();
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({
    date: "",
    startTime: "",
    endTime: "",
    doctorId: "",
  });

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const res = await listClinicDoctors({ token }).unwrap();
        const list = res?.data?.users || res?.data?.doctors || [];
        if (active) setDoctorOptions(list);
      } catch (err) {
        if (active) setDoctorOptions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, listClinicDoctors]);

  useEffect(() => {
    if (!selectedAppointment) return;
    const s = selectedAppointment.startAt
      ? new Date(selectedAppointment.startAt)
      : null;
    const e = selectedAppointment.endAt
      ? new Date(selectedAppointment.endAt)
      : null;
    const pad = (n) => String(n).padStart(2, "0");
    setScheduleForm({
      date: s ? `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}` : "",
      startTime: s ? `${pad(s.getHours())}:${pad(s.getMinutes())}` : "",
      endTime: e ? `${pad(e.getHours())}:${pad(e.getMinutes())}` : "",
      doctorId: getRefId(selectedAppointment.doctor) || "",
    });
  }, [selectedAppointment, getRefId]);

  const handleScheduleSave = async () => {
    if (!selectedAppointment?._id) return;
    const { date, startTime, endTime, doctorId } = scheduleForm;
    if (!date || !startTime || !endTime || !doctorId) {
      showError("Tüm alanlar zorunludur");
      return;
    }
    const startAt = new Date(`${date}T${startTime}:00`);
    const endAt = new Date(`${date}T${endTime}:00`);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      showError("Geçersiz tarih/saat");
      return;
    }
    try {
      await updateSchedule({
        token,
        appointmentId: selectedAppointment._id,
        doctorId,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
      }).unwrap();
      showSuccess("Randevu güncellendi");
      onClose?.();
    } catch (err) {
      const msg =
        err?.data?.message || err?.error || "Randevu güncellenemedi";
      showError(msg);
    }
  };

  const handleMuayene = async () => {
    if (!selectedAppointment?._id) return;
    try {
      if (selectedAppointment.status === "scheduled") {
        await startExamination({
          token,
          appointmentId: selectedAppointment._id,
        }).unwrap();
      }
      onClose?.();
      navigate(`/Randevular/${selectedAppointment._id}/Muayene`);
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.error ||
        `${terms.exam} başlatılamadı`;
      showError(msg);
    }
  };

  if (!selectedAppointment) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
        <EmptyState variant="inline" icon={<EventNoteIcon />} title="Randevu seçilmedi." />
      </Drawer>
    );
  }

  const a = selectedAppointment;
  const typeMeta = getAppointmentTypeMeta(a.appointmentType);
  const statusInfo = getStatusInfo(a.status, terms);
  const isCancelled = String(a.status || "").toLowerCase() === "cancelled";

  const dateStr = a.startAt
    ? new Date(a.startAt).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "-";
  const timeStr = a.startAt
    ? new Date(a.startAt).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
  const endTimeStr = a.endAt
    ? new Date(a.endAt).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const doctorName = a.doctor
    ? getDoctorName(getRefId(a.doctor))
    : staffLabel({ doctorRoleName: a?.snapshot?.doctorRoleName, name: a?.snapshot?.doctorName }) || "-";
  const patientName = a?.snapshot?.patientName || subtitle || null;

  // Tahlil isimleri
  const testNames = (a.labTestIds || [])
    .map((id) => {
      const test = (labTests || []).find((t) => String(t._id) === String(id));
      return test?.name || null;
    })
    .filter(Boolean);

  // İptal bilgileri
  const cancelReason = a.cancellation?.reason || a.cancelReason || null;
  const cancelledAt = a.cancellation?.cancelledAt || null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          bgcolor: COLORS.slate50,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* ── Header ── */}
        <DrawerHeader
          icon={<EventNoteIcon />}
          title="Randevu Detayı"
          subtitle={patientName || dateStr}
          onClose={onClose}
          bgcolor={isCancelled ? COLORS.errorDark : undefined}
        />

        {/* ── Content ── */}
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", flex: 1 }}>
          {/* Tür + durum rozetleri */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={typeMeta.label}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: alpha(palette.primary, 0.08),
                color: palette.primary,
                border: `1px solid ${alpha(palette.primary, 0.25)}`,
              }}
            />
            <Chip
              label={statusInfo.label}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: statusInfo.bg,
                color: statusInfo.color,
                border: `1px solid ${alpha(statusInfo.color, 0.25)}`,
              }}
            />
            {a.patientConfirmation?.status === "confirmed" ? (
              <Chip
                label={`${terms.patient} onayladı (WhatsApp)`}
                size="small"
                sx={{
                  fontWeight: 500,
                  bgcolor: alpha(COLORS.successDark, 0.08),
                  color: COLORS.successDarker,
                  border: `1px solid ${alpha(COLORS.successDark, 0.25)}`,
                }}
              />
            ) : null}
          </Box>

          {/* Hasta (takvimden geliyorsa göster) */}
          {patientName && (
            <Box
              sx={{
                bgcolor: COLORS.white,
                borderRadius: 2.5,
                border: `1px solid ${COLORS.slate200}`,
                px: 2.5,
                py: 1,
              }}
            >
              <InfoRow
                icon={<PersonIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                label={terms.patient}
                value={patientName}
              />
            </Box>
          )}

          {/* Tarih / Saat / Doktor */}
          {(() => {
            const isScheduled =
              !isCancelled &&
              canUpdateAppointment &&
              String(a.status || "").toLowerCase() === "scheduled";

            if (!isScheduled) {
              return (
                <Box
                  sx={{
                    bgcolor: COLORS.white,
                    borderRadius: 2.5,
                    border: `1px solid ${COLORS.slate200}`,
                    px: 2.5,
                    py: 1,
                  }}
                >
                  <InfoRow
                    icon={<CalendarIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                    label="Tarih"
                    value={dateStr}
                  />
                  <Divider />
                  <InfoRow
                    icon={<ClockIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                    label="Saat"
                    value={endTimeStr ? `${timeStr} - ${endTimeStr}` : timeStr}
                  />
                  <Divider />
                  <InfoRow
                    icon={<PersonIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                    label={terms.doctor}
                    value={doctorName}
                  />
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  bgcolor: COLORS.white,
                  borderRadius: 2.5,
                  border: `1px solid ${COLORS.slate200}`,
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <FormDatePicker
                  label="Tarih"
                  variant="standard"
                  fullWidth
                  value={scheduleForm.date}
                  onChange={(v) => setScheduleForm((f) => ({ ...f, date: v }))}
                />
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <FormTextField
                    palette={palette}
                    label="Başlangıç"
                    type="time"
                    size="small"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={scheduleForm.startTime}
                    onChange={(e) =>
                      setScheduleForm((f) => ({
                        ...f,
                        startTime: e.target.value,
                      }))
                    }
                  />
                  <FormTextField
                    palette={palette}
                    label="Bitiş"
                    type="time"
                    size="small"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={scheduleForm.endTime}
                    onChange={(e) =>
                      setScheduleForm((f) => ({
                        ...f,
                        endTime: e.target.value,
                      }))
                    }
                  />
                </Box>
                <FormSelect
                  palette={palette}
                  label={terms.doctor}
                  variant="standard"
                  placeholder={null}
                  InputLabelProps={{ shrink: true }}
                  value={scheduleForm.doctorId}
                  onChange={(v) =>
                    setScheduleForm((f) => ({
                      ...f,
                      doctorId: v,
                    }))
                  }
                >
                  {doctorOptions.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      {`${d.name || ""} ${d.surname || ""}`.trim() || d.email}
                    </MenuItem>
                  ))}
                </FormSelect>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                  <ActionButton
                    variant="save"
                    compact
                    palette={palette}
                    label={isSavingSchedule ? "Güncelleniyor..." : "Güncelle"}
                    loading={isSavingSchedule}
                    onClick={handleScheduleSave}
                  />
                </Box>
              </Box>
            );
          })()}

          {/* Muayene aksiyonu */}
          {(() => {
            if (!examAction.mode && !examAction.note) return null;
            const label =
              examAction.mode === "start" && isStartingExamination ? "Başlatılıyor..." : examAction.label;
            const variant = examAction.variant;
            return (
              <Box
                sx={{
                  bgcolor: COLORS.catPurpleLight,
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(COLORS.catPurpleDark, 0.2)}`,
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <FaStethoscope size={18} color={COLORS.catPurpleDark} />
                <Typography sx={{ flex: 1, fontWeight: 600, color: COLORS.catPurpleDark, fontSize: TYPO.body }}>
                  {terms.exam}
                </Typography>
                {examAction.mode ? (
                  <ActionButton
                    variant={variant}
                    label={label}
                    compact
                    loading={isStartingExamination}
                    onClick={handleMuayene}
                  />
                ) : (
                  <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate500 }}>
                    {examAction.note}
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Hizmet / paket (billing modülü; kapalıysa çizilmez) */}
          <AppointmentBillingSection appointment={a} />

          {/* Şikayet & Not (salt okunur) */}
          {!extraContent && (
            <Box
              sx={{
                bgcolor: COLORS.white,
                borderRadius: 2.5,
                border: `1px solid ${COLORS.slate200}`,
                px: 2.5,
                py: 1,
              }}
            >
              <InfoRow
                icon={<ComplaintIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                label="Şikayet"
                value={a.complaint || "Belirtilmemiş"}
              />
              <Divider />
              <InfoRow
                icon={<NotesIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />}
                label="Not"
                value={a.notes || "Not yok"}
              />
            </Box>
          )}

          {/* Ek içerik (düzenleme alanları vb.) */}
          {extraContent}

          {/* Tahliller */}
          {testNames.length > 0 && (
            <Box
              sx={{
                bgcolor: COLORS.white,
                borderRadius: 2.5,
                border: `1px solid ${COLORS.slate200}`,
                px: 2.5,
                py: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(ACCENTS.violet, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LabIcon sx={{ fontSize: 18, color: ACCENTS.violet }} />
                </Box>
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400, fontWeight: 700 }}>
                  Tahliller
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {testNames.map((name, i) => (
                  <Chip
                    key={i}
                    label={name}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(ACCENTS.violet, 0.08),
                      color: COLORS.catPurpleDark,
                      border: `1px solid ${alpha(ACCENTS.violet, 0.25)}`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* İptal bilgileri */}
          {isCancelled && (
            <Box
              sx={{
                bgcolor: COLORS.errorLightest,
                borderRadius: 2.5,
                border: `1px solid ${COLORS.errorLight}`,
                px: 2.5,
                py: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: COLORS.errorLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BlockIcon sx={{ fontSize: 18, color: COLORS.errorDark }} />
                </Box>
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark, fontWeight: 800 }}>
                  İptal Bilgileri
                </Typography>
              </Box>

              {cancelReason && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark, fontWeight: 700 }}>
                    Neden
                  </Typography>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.errorDark }}>
                    {cancelReason}
                  </Typography>
                </Box>
              )}

              {cancelledAt && (
                <Box>
                  <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark, fontWeight: 700 }}>
                    İptal Tarihi
                  </Typography>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.errorDark }}>
                    {new Date(cancelledAt).toLocaleDateString("tr-TR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
              )}

              {!cancelReason && !cancelledAt && (
                <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.errorDark }}>
                  Bu randevu iptal edilmiş.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        {footer}
      </Box>
    </Drawer>
  );
}
